// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { CPT, createReference, formatHumanName, getExtension, getReferenceString } from '@medplum/core';
import type {
  Appointment,
  BundleEntry,
  ChargeItem,
  ClinicalImpression,
  Coding,
  Encounter,
  HumanName,
  Patient,
  Practitioner,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import { ensurePractitionerOnCareTeam, resolveChargeForAppointmentType } from '@polarissleep/scheduling';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  isBundleCoveredAppointment,
  isSelfPayPatient,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { completeWorkflowTask } from '../../bots/services/workflow/complete-workflow-task';
import { getProviderAppEnvConfig } from '../config/env';
import { APPOINTMENT_TO_WORKFLOW_ACTION } from '../constants/workflows';
import type { AppointmentTypeDefinition } from '../types/appointmentTypes';
import { withTenantScope } from './tenant';

export async function createEncounter(
  medplum: MedplumClient,
  start: Date,
  end: Date,
  patient: Patient,
  appointmentType?: AppointmentTypeDefinition,
  practitioner?: Practitioner
): Promise<Encounter> {
  const selectedPractitioner = practitioner ?? (medplum.getProfile() as Practitioner);
  const coding = appointmentType?.coding;
  const suppressForBundle = await isBundleCoveredAppointment(medplum, patient, coding?.code ?? '', {
    appointmentStart: start.toISOString(),
  });
  const deferInsuranceBilling = getProviderAppEnvConfig().insuranceDeferredBillingEnabled && !isSelfPayPatient(patient);

  let appointment: Appointment | undefined;
  let encounter: Encounter | undefined;
  let clinicalImpression: ClinicalImpression | undefined;
  try {
    appointment = await medplum.createResource(
      withTenantScope({
        resourceType: 'Appointment',
        status: 'booked',
        start: start.toISOString(),
        end: end.toISOString(),
        appointmentType: coding ? { coding: [coding], text: coding.display } : undefined,
        participant: [
          {
            actor: {
              reference: getReferenceString(patient),
              display: formatHumanName(patient.name?.[0] as HumanName),
            },
            status: 'accepted',
          },
          {
            actor: {
              reference: getReferenceString(selectedPractitioner),
              display: formatHumanName(selectedPractitioner.name?.[0] as HumanName),
            },
            status: 'accepted',
          },
        ],
      })
    );

    encounter = await medplum.createResource(
      withTenantScope({
        resourceType: 'Encounter',
        status: 'planned',
        class: {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: 'VR',
          display: 'virtual',
        },
        type: coding ? [{ coding: [coding], text: coding.display }] : undefined,
        subject: createReference(patient),
        appointment: [createReference(appointment)],
        participant: [
          {
            individual: {
              reference: getReferenceString(selectedPractitioner),
            },
          },
        ],
      })
    );

    await ensurePractitionerOnCareTeam(medplum, patient, selectedPractitioner, {
      decorate: withTenantScope as <T extends { resourceType: string }>(resource: T) => T,
    });

    clinicalImpression = await medplum.createResource(
      withTenantScope({
        resourceType: 'ClinicalImpression',
        status: 'in-progress',
        description: 'Initial clinical impression',
        subject: createReference(patient),
        encounter: createReference(encounter),
        date: new Date().toISOString(),
      })
    );

    if (!suppressForBundle && !deferInsuranceBilling) {
      await createChargeItemFromAppointmentType(medplum, encounter, patient, appointment, start, appointmentType);
    }
    await handleChargeItemsFromTasks(medplum, encounter, patient);

    const apptCode = coding?.code;
    if (apptCode && patient.id) {
      const workflowActionId = APPOINTMENT_TO_WORKFLOW_ACTION[apptCode];
      if (workflowActionId) {
        await completeWorkflowTask(medplum, patient.id, workflowActionId).catch((err) =>
          console.error(`[Encounter] Failed to complete workflow task "${workflowActionId}":`, err)
        );
      }
    }

    return encounter;
  } catch (err) {
    if (clinicalImpression?.id) {
      await medplum.deleteResource('ClinicalImpression', clinicalImpression.id).catch(() => undefined);
    }
    if (encounter?.id) {
      await medplum.deleteResource('Encounter', encounter.id).catch(() => undefined);
    }
    if (appointment?.id) {
      await medplum.deleteResource('Appointment', appointment.id).catch(() => undefined);
    }

    throw err;
  }
}

const CHARGEITEM_MODIFIER_URL = 'http://hl7.org/fhir/StructureDefinition/chargeitem-modifier';
const ESTIMATED_AMOUNT_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/estimated-charge-amount';

function isGoneError(error: unknown): boolean {
  return error instanceof Error && error.message.toLowerCase().includes('gone');
}

async function createChargeItemFromAppointmentType(
  medplum: MedplumClient,
  encounter: Encounter,
  patient: Patient,
  appointment: Appointment,
  start: Date,
  appointmentType?: AppointmentTypeDefinition
): Promise<void> {
  if (!appointmentType) {
    return;
  }

  let chargeSpec;
  try {
    chargeSpec = await resolveChargeForAppointmentType(medplum, appointmentType, patient, start.toISOString());
  } catch (err) {
    Sentry.captureException(err, {
      extra: { billingCode: appointmentType.billingCode, encounterId: encounter.id, patientId: patient.id },
    });
    return;
  }

  if (!chargeSpec) {
    return; // non-chargeable appointment type — no ChargeItem needed
  }

  const { billingModifier, display } = appointmentType;

  const chargeItem: ChargeItem = {
    resourceType: 'ChargeItem',
    meta: {
      tag: [{ system: 'http://polarissleep.com/charge-item-source', code: 'auto-created' }],
    },
    status: 'planned',
    subject: createReference(patient),
    context: createReference(encounter),
    service: [createReference(appointment)] as unknown as ChargeItem['service'],
    occurrenceDateTime: start.toISOString(),
    code: {
      coding: [{ system: CPT, code: chargeSpec.cptCode, display }],
    },
    quantity: { value: 1 },
    definitionCanonical: [chargeSpec.chargeItemDefinitionUrl],
    identifier: [{ system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: appointment.id ?? '' }],
    priceOverride: { value: chargeSpec.amountUsd, currency: 'USD' },
    extension: [
      {
        url: ESTIMATED_AMOUNT_EXTENSION_URL,
        valueMoney: { value: chargeSpec.amountUsd, currency: 'USD' },
      },
      ...(billingModifier
        ? [
            {
              url: CHARGEITEM_MODIFIER_URL,
              valueCodeableConcept: { coding: [{ system: CPT, code: billingModifier }] },
            },
          ]
        : []),
    ],
  };

  await medplum.createResource(withTenantScope(chargeItem));
}

async function handleChargeItemsFromTasks(
  medplum: MedplumClient,
  encounter: Encounter,
  patient: Patient
): Promise<void> {
  const tasks = await medplum.search('Task', {
    encounter: getReferenceString(encounter),
  });

  if (!tasks.entry?.length) {
    return;
  }

  await Promise.all(
    tasks.entry.map(async (entry: BundleEntry) => {
      const task = entry.resource as Task;
      const serviceRequestRef = task.focus?.reference;

      if (!serviceRequestRef?.startsWith('ServiceRequest/')) {
        return;
      }

      try {
        const serviceRequest: ServiceRequest = await medplum.readReference({
          reference: serviceRequestRef,
        });
        await createChargeItemFromServiceRequest(medplum, patient, serviceRequest);
      } catch (err) {
        console.error(`Error processing ServiceRequest ${serviceRequestRef}:`, err);
      }
    })
  );
}

async function createChargeItemFromServiceRequest(
  medplum: MedplumClient,
  patient: Patient,
  serviceRequest: ServiceRequest
): Promise<void> {
  const chargeDefinitionExtension = getExtension(
    serviceRequest,
    'http://medplum.com/fhir/StructureDefinition/applicable-charge-definition'
  );

  if (
    !chargeDefinitionExtension?.valueCanonical ||
    !serviceRequest.code?.coding?.find((c: Coding) => c.system === 'http://www.ama-assn.org/go/cpt')
  ) {
    return;
  }

  const canonicalUrl = chargeDefinitionExtension?.valueCanonical;
  const definitionCanonical = canonicalUrl ? [canonicalUrl] : [];

  const chargeItem: ChargeItem = {
    resourceType: 'ChargeItem',
    status: 'planned',
    supportingInformation: [
      {
        reference: `ServiceRequest/${serviceRequest.id}`,
      },
    ],
    subject: createReference(patient),
    context: serviceRequest.encounter,
    occurrenceDateTime: serviceRequest.occurrenceDateTime || new Date().toISOString(),
    code: serviceRequest.code || { coding: [] },
    quantity: {
      value: 1,
    },
    definitionCanonical: definitionCanonical,
  };

  await medplum.createResource(withTenantScope(chargeItem));
}

/**
 * Encounter statuses a visit can still be concluded from. Excludes `finished`
 * (already concluded), `cancelled` / `entered-in-error` (terminal — a note may
 * still be written for a no-show, and that must never become a completed visit),
 * and `unknown`.
 */
const CONCLUDABLE_ENCOUNTER_STATUSES: readonly Encounter['status'][] = [
  'planned',
  'arrived',
  'triaged',
  'in-progress',
  'onleave',
];

/**
 * Human label for a visit that is already over, or undefined while it is still
 * concludable. Drives the "Concluded" / "Cancelled" / "No-show" indicator that
 * replaces the Conclude button, so a visit that cannot be concluded again always
 * says why rather than showing a dead control.
 *
 * The Encounter carries the outcome for cancellations; only the Appointment
 * distinguishes a no-show from a plain cancellation.
 * @param encounter - The Encounter to label
 * @param appointment - The linked Appointment, if any
 * @returns A label, or undefined when the visit can still be concluded
 */
export function getVisitOutcomeLabel(encounter: Encounter, appointment: Appointment | undefined): string | undefined {
  if (CONCLUDABLE_ENCOUNTER_STATUSES.includes(encounter.status)) {
    return undefined;
  }
  if (encounter.status === 'finished') {
    return 'Concluded';
  }
  if (appointment?.status === 'noshow') {
    return 'No-show';
  }
  if (encounter.status === 'cancelled') {
    return 'Cancelled';
  }
  return 'Not concludable';
}

/**
 * Concludes a visit as successful because its note was signed and locked.
 *
 * Signing and locking a note is a stronger statement than concluding the session
 * — the clinician has legally authenticated the record — so leaving the Encounter
 * unfinished afterwards stranded the visit: `Appointment.status` never reached
 * `fulfilled`, and the `Encounter?status=finished` Subscription that submits the
 * claim never fired. This closes that gap automatically.
 *
 * No-ops when the visit is already concluded or is in a terminal state, so a
 * cancelled or no-showed appointment is never resurrected as completed.
 * @param medplum - Medplum client
 * @param encounter - The Encounter whose note was just locked
 * @param appointment - The linked Appointment, if any
 * @returns The updated Encounter, or undefined when no change was needed
 */
export async function concludeEncounterForLockedNote(
  medplum: MedplumClient,
  encounter: Encounter,
  appointment: Appointment | undefined
): Promise<Encounter | undefined> {
  if (!CONCLUDABLE_ENCOUNTER_STATUSES.includes(encounter.status)) {
    return undefined;
  }
  return updateEncounterStatus(medplum, encounter, appointment, 'finished');
}

export async function updateEncounterStatus(
  medplum: MedplumClient,
  encounter: Encounter,
  appointment: Appointment | undefined,
  newStatus: Encounter['status'],
  options?: { noshow?: boolean; outcome?: string }
): Promise<Encounter> {
  const updatedEncounter: Encounter = {
    ...encounter,
    status: newStatus,
    ...(newStatus === 'in-progress' &&
      !encounter.period?.start && {
        period: {
          ...encounter.period,
          start: new Date().toISOString(),
        },
      }),
    ...(newStatus === 'finished' &&
      !encounter.period?.end && {
        period: {
          ...encounter.period,
          end: new Date().toISOString(),
        },
      }),
  };

  if (appointment) {
    const updatedAppointment: Appointment = appointment;
    const previousAppointmentStatus = appointment.status;
    const previousCancellationReason = appointment.cancelationReason?.text;
    switch (newStatus) {
      case 'cancelled':
        // No-show: encounter is cancelled (no visit) but appointment is marked 'noshow' for billing/reporting
        updatedAppointment.status = options?.noshow ? 'noshow' : 'cancelled';
        if (options?.outcome === 'rescheduled' || options?.outcome === 'technical-issues') {
          updatedAppointment.cancelationReason = { text: options.outcome };
        }
        break;
      case 'finished':
        updatedAppointment.status = 'fulfilled';
        break;
      default:
        // The in-person 'arrived' and 'checked-in' appointment states aren't used
        // by a virtual clinic and nothing ever set them, so an Encounter moving to
        // 'arrived' or 'in-progress' leaves the Appointment 'booked' until it is
        // concluded. Appointment lifecycle: pending -> booked -> fulfilled |
        // cancelled | noshow.
        break;
    }
    try {
      const appointmentStatusChanged = updatedAppointment.status !== previousAppointmentStatus;
      const cancellationReasonChanged = updatedAppointment.cancelationReason?.text !== previousCancellationReason;
      if (appointmentStatusChanged || cancellationReasonChanged) {
        await medplum.updateResource<Appointment>(updatedAppointment);
      }
    } catch (error) {
      if (!isGoneError(error)) {
        throw error;
      }
    }
  }

  return medplum.updateResource(updatedEncounter);
}
